import * as Sentry from '@sentry/vue'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { LocalNotifications } from '@capacitor/local-notifications'
import { PlatformService } from './index'

const ANDROID_CHANNEL_ID = 'volontapp'

// Push via Firebase Cloud Messaging (app >= 1.4.0, replaces onesignal-cordova-plugin).
// Public shape is unchanged for store/modules/user.js: handlers receive
// { notification: { additionalData } } and getDeviceState() returns
// { hasNotificationPermission, userId } where userId is the FCM registration token.
export const PushService = {
	initialized: false,

	/**
	 * @param {Function} onNotificationOpened called with { notification: { additionalData } } on tap
	 * @param {Function} onTokenRefreshed called with the FCM token whenever FCM issues/refreshes it
	 *   (on iOS the first token often arrives here, after APNs registration, not from getToken())
	 */
	async initialize(onNotificationOpened, onTokenRefreshed) {
		if (this.initialized || !PlatformService.isNative()) return
		this.initialized = true

		const opened = (data) => {
			if (onNotificationOpened) onNotificationOpened({ notification: { additionalData: data || {} } })
		}

		try {
			await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
				opened(event.notification?.data)
			})

			// FCM does not display notifications while the app is in foreground on Android
			// (OneSignal did): mirror them as local notifications. iOS uses presentationOptions.
			await FirebaseMessaging.addListener('notificationReceived', (event) => {
				if (PlatformService.isAndroid()) this.showInForeground(event.notification)
			})
			if (PlatformService.isAndroid()) {
				await LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
					opened(event.notification?.extra)
				})
			}

			await FirebaseMessaging.addListener('tokenReceived', (event) => {
				if (onTokenRefreshed && event.token) Promise.resolve(onTokenRefreshed(event.token)).catch(() => {})
			})
		} catch (e) {
			// listeners are best-effort: a failure here must never break login
			Sentry.captureException(e)
		}

		if (PlatformService.isAndroid()) {
			try {
				// High importance is required for the foreground mirror to show a heads-up
				// banner; without a dedicated channel Capacitor falls back to its own
				// "Default" channel at IMPORTANCE_DEFAULT, which shows no banner.
				await FirebaseMessaging.createChannel({
					id: ANDROID_CHANNEL_ID,
					name: 'Notifiche VolontApp',
					description: 'Avvisi su turni, richieste e bacheca',
					importance: 5,
					visibility: 1
				})
			} catch (e) {
				// channel creation is best-effort: a failure here must never break login
				Sentry.captureException(e)
			}
		}

		try {
			await FirebaseMessaging.requestPermissions()
		} catch (e) {
			// a failed prompt is not fatal: getDeviceState() reports the real permission state
			Sentry.captureException(e)
		}
	},

	async getDeviceState() {
		if (!PlatformService.isNative()) return null

		try {
			const { receive } = await FirebaseMessaging.checkPermissions()
			if (receive !== 'granted') {
				return { hasNotificationPermission: false, userId: null }
			}
			const { token } = await FirebaseMessaging.getToken()
			return { hasNotificationPermission: true, userId: token || null }
		} catch (e) {
			// on iOS getToken() can fail until APNs registration completes: tokenReceived covers it
			Sentry.captureException(e)
			return null
		}
	},

	async showInForeground(notification) {
		// Data-only pushes (including any leftover OneSignal delivery) have no title/body: ignore them.
		if (!notification || (!notification.title && !notification.body)) return

		try {
			await LocalNotifications.schedule({
				notifications: [{
					id: Date.now() % 2147483647,
					title: notification.title || 'VolontApp',
					body: notification.body || '',
					extra: notification.data || {},
					channelId: ANDROID_CHANNEL_ID
				}]
			})
		} catch (e) {
			// never break the app for a foreground banner
			Sentry.captureException(e)
		}
	}
}
